import { useEffect, useRef, useCallback } from 'react';
import { useNostr } from '@nostrify/react';
import { NSecSigner } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import {
  ensureMerchantIdentity,
  canPublishHeartbeat,
  recordHeartbeat,
  buildActivityHeartbeat,
  buildPaymentHeartbeat,
  buildMerchantListing,
  type MerchantProfile,
} from '@/lib/merchantNostr';
import { loadSettings } from '@/lib/posStorage';

/**
 * Auto-publishes merchant activity heartbeat + listing when enabled.
 * Runs once on mount, respects 24h throttle, and is entirely silent on failure.
 */
export function useMerchantHeartbeat() {
  const { nostr } = useNostr();
  const publishedRef = useRef(false);

  useEffect(() => {
    if (publishedRef.current) return;

    const settings = loadSettings();
    if (!settings.publishActivity) return;
    if (!canPublishHeartbeat()) return;

    publishedRef.current = true;

    const publish = async () => {
      try {
        const identity = ensureMerchantIdentity();
        const decoded = nip19.decode(identity.nsec);
        if (decoded.type !== 'nsec') return;
        const signer = new NSecSigner(decoded.data);

        // Publish heartbeat
        const heartbeatTemplate = buildActivityHeartbeat();
        const heartbeatEvent = await signer.signEvent({
          kind: heartbeatTemplate.kind,
          content: heartbeatTemplate.content,
          tags: heartbeatTemplate.tags,
          created_at: Math.floor(Date.now() / 1000),
        });
        await nostr.event(heartbeatEvent, { signal: AbortSignal.timeout(10000) });
        recordHeartbeat();

        // Also publish/update the listing
        const profile: MerchantProfile = {
          name: settings.merchantName || 'BCH Merchant',
          description: settings.merchantTagline || undefined,
          category: settings.merchantCategory || undefined,
          website: settings.merchantWebsite || undefined,
          geohash: settings.merchantGeohash || undefined,
        };
        const listingTemplate = buildMerchantListing(profile);
        const listingEvent = await signer.signEvent({
          kind: listingTemplate.kind,
          content: listingTemplate.content,
          tags: listingTemplate.tags,
          created_at: Math.floor(Date.now() / 1000),
        });
        await nostr.event(listingEvent, { signal: AbortSignal.timeout(10000) });
      } catch {
        // Silent failure — merchant discovery is best-effort
      }
    };

    publish();
  }, [nostr]);
}

/**
 * Returns a callback that publishes a kind 7531 payment heartbeat to Nostr.
 * Call it once per completed sale. Fire-and-forget — silent on failure.
 * Does nothing if the merchant hasn't opted into activity publishing.
 */
export function usePublishPaymentHeartbeat() {
  const { nostr } = useNostr();

  return useCallback(async () => {
    try {
      const settings = loadSettings();
      if (!settings.publishActivity) return;

      const identity = ensureMerchantIdentity();
      const decoded = nip19.decode(identity.nsec);
      if (decoded.type !== 'nsec') return;
      const signer = new NSecSigner(decoded.data);

      const template = buildPaymentHeartbeat();
      const event = await signer.signEvent({
        kind: template.kind,
        content: template.content,
        tags: template.tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      await nostr.event(event, { signal: AbortSignal.timeout(10000) });
    } catch {
      // Silent failure — best-effort
    }
  }, [nostr]);
}
